import { useReactiveVar } from "@apollo/client";
import { Input, RatingStar, Textarea } from "@dmm-com/rocket-web-ui-library";
import { useMemo } from "react";
import { Supplement } from "../supplement";
import * as Styled from "./style";
import { usePostReview } from "./use-post-review";
import { ItemPackage } from "~/components/blocks/common/item-package";
import { PortalModal } from "~/components/blocks/common/portal-modal";
import { isOpenReviewModalVar } from "~/components/stores/detail-product/store";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { useMatchMedia } from "~/hooks/use-match-media";
import type { G_GetVideoQuery, G_Reviewer, Maybe } from "~/lib/graphql";
import { Z_INDEX } from "~/styles/z-index";

type PostReviewModalProps = {
  /** videoクエリを叩いて取得したデータ */
  videoData: G_GetVideoQuery["video"];
  /** レビュワー情報 */
  reviewer: Maybe<Pick<G_Reviewer, "id" | "nickname" | "videoReviewStatus">> | undefined;
};

export const PostReviewModal = (props: PostReviewModalProps) => {
  const { videoData, reviewer } = props;

  const isOpenReviewModal = useReactiveVar(isOpenReviewModalVar);

  const { handleClickByDevice } = useHandleClickByDevice();
  const { isSpSize } = useMatchMedia();

  /** modal画面サイズ判定 */
  const modalSize = useMemo(() => (!isSpSize ? "middle" : "fullScreen"), [isSpSize]);

  /** ニックネームが空文字の場合は初回投稿、空文字でない場合は既投稿者 */
  const reviewerNickName = useMemo(() => {
    if (reviewer === null || reviewer === undefined) return "";

    const { nickname } = reviewer;

    if (nickname === null || nickname === undefined || nickname === "") return "";

    return nickname;
  }, [reviewer]);

  const {
    checkedArray,
    nickName,
    starScore,
    title,
    textareaValue,
    isPostReviewBtnDisabled,
    addReviewHandler,
    onStarIconClicked,
    onChangeCheckbox,
    onChangeInputNickNameValue,
    onChangeInputTitleValue,
    onChangeTextareaValue,
    onClickNickNameClearButton,
    onClickTitleClearButton,
  } = usePostReview({ reviewerNickName });

  return (
    <PortalModal
      size={modalSize}
      basisZIndex={Z_INDEX.MODAL_IN_MODAL}
      isOpen={isOpenReviewModal}
      onCloseModal={() => isOpenReviewModalVar(false)}
      headerConfig={{
        isShow: true,
        headerTitle: <Styled.Heading>レビューの投稿</Styled.Heading>,
      }}
    >
      <Styled.DivContentWrapper>
        <Styled.TitleWrapper>
          <ItemPackage
            alt={videoData.titleName}
            src={videoData.packageImage}
            width="128px"
            height="176px"
            borderRadius="8px"
            aspectRatio="7/5"
          />
          <p>{videoData.titleName}</p>
        </Styled.TitleWrapper>

        <Styled.ScoreWrapper>
          <p>
            評価<Styled.SpanMandatory>*</Styled.SpanMandatory>
          </p>
          <RatingStar starScore={starScore} size="XXL" onStarIconClicked={onStarIconClicked} />
        </Styled.ScoreWrapper>

        {reviewerNickName === "" && (
          <Styled.DivContainerWrapper>
            <p>
              ニックネーム<Styled.SpanMandatory>*</Styled.SpanMandatory>
            </p>
            <Styled.ParNoteMessage>※レビュー、マーケットプレイス、オークションで使用します。</Styled.ParNoteMessage>
            <Input
              type="text"
              name="nickName"
              value={nickName.value}
              placeholder="ニックネーム（〜12文字）"
              onChange={onChangeInputNickNameValue}
              maxCountLength={12}
              onClickClearButton={onClickNickNameClearButton}
              isError={nickName.error}
              errorText={nickName.errorText}
            />
          </Styled.DivContainerWrapper>
        )}

        <Styled.DivContainerWrapper>
          <p>タイトル</p>
          <Input
            type="text"
            name="title"
            value={title.value}
            placeholder="伝えたいポイントを教えてください（〜32文字）"
            onChange={onChangeInputTitleValue}
            maxCountLength={32}
            onClickClearButton={onClickTitleClearButton}
            isError={title.error}
            errorText={title.errorText}
          />
        </Styled.DivContainerWrapper>

        <Styled.DivContainerWrapper>
          <p>コメント</p>
          <Textarea
            textareaValue={textareaValue.value}
            maxTextCount={1000}
            onTextAreaChange={onChangeTextareaValue}
            placeholder="おすすめしたいこと / おすすめできないことはありますか？（30〜1000文字）"
            isError={textareaValue.error}
            errorText={textareaValue.errorText}
          />
        </Styled.DivContainerWrapper>

        <Styled.DivSpoiler>
          <Styled.Checkbox
            labelText="ネタバレ"
            name="spoiler"
            value="spoiler"
            isChecked={checkedArray.includes("spoiler")}
            onCheckboxChange={onChangeCheckbox}
            isOnly
          />
          <Styled.ParSpoilerHelperText>
            スタッフの判断により『ネタバレ』とさせていただく場合があります。
          </Styled.ParSpoilerHelperText>
        </Styled.DivSpoiler>

        <Styled.DivSubmit>
          <Styled.PostButton
            name="post-review"
            mainText="投稿"
            disabled={isPostReviewBtnDisabled}
            {...handleClickByDevice(addReviewHandler)}
          />

          <Styled.ParSubmitHelperText>
            <Styled.AnchorTextLink href="https://terms.dmm.com/review_service/" target="_blank" color="BLUE900">
              レビュー利用規約
            </Styled.AnchorTextLink>
            に同意の上、投稿してください。
          </Styled.ParSubmitHelperText>

          <Styled.ParSubmitHelperText>
            投稿いただいたレビューは、スタッフ確認後に通常1週間ほどで公開されます。
          </Styled.ParSubmitHelperText>
        </Styled.DivSubmit>

        <Styled.DivNotes>
          <Supplement />
        </Styled.DivNotes>
      </Styled.DivContentWrapper>
    </PortalModal>
  );
};
